import { createSelector } from 'reselect';

import { getPlanErknmStatusByStatusTitle } from 'APIPackage/PlanListModule/common/getPlanErknmStatusByStatusTitle';
import { getPlanErknmStatusTitleByStatus } from 'APIPackage/PlanListModule/common/getPlanErknmStatusTitleByStatus';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { UserRoleEnum } from 'APIPackage/AuthModule/enums/UserRoleEnum';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { checkValueInEnum } from 'CorePackage/MainModule/common/checkValueInEnum';
import { getUserProfileIsTest } from 'AuthPackage/MainModule/selectors/authSelectors';
import {
  getDictionariesKnoSupervisionUuidSelector,
  makeGetDictionariesPositionsSelectOptions,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';

import { IPlanHistoryItem } from '../interfaces/IPlanHistoryItem';

export const getPlan = (state: IRootState) => state.plan;
export const getPlanIsLoadingDocuments = (state: IRootState) => getPlan(state).isLoadingDocuments;
export const getPlanIsLoadingHistory = (state: IRootState) => getPlan(state).isLoadingHistory;
export const getPlanData = (state: IRootState) => getPlan(state).data;
export const getPlanWatchOnlyByTestUser = (state: IRootState) => {
  const isUserTest: boolean | undefined = getUserProfileIsTest(state);
  const isTest = !!getPlanData(state)?.isTest;

  return isUserTest ? !isTest : false;
};
export const getPlanDataErknmStatusTitle = (state: IRootState) => getPlanData(state)?.erknmStatus;
export const getPlanDataErknmStatus = (state: IRootState) => {
  const planErknmStatusTitle = getPlanDataErknmStatusTitle(state);

  return getPlanErknmStatusByStatusTitle(planErknmStatusTitle);
};
export const getPlanDataKnmPlanData = (state: IRootState) => getPlanData(state)?.knmPlanData;
export const getPlanDataFullNameWhoConsideredPlan = (state: IRootState) =>
  getPlanDataKnmPlanData(state)?.fullNameWhoConsideredPlan;
export const getPlanDataPositionWhoConsideredPlan = (state: IRootState) =>
  getPlanDataKnmPlanData(state)?.positionWhoConsideredPlan;
export const getPlanInfo = (state: IRootState) => getPlan(state).planInfo;
export const getPlanSupervisionTypeValue = (state: IRootState) => getPlan(state).supervisionTypeValue;
export const getPlanDocuments = (state: IRootState) => getPlan(state).planDocuments;
export const getPlanDataKnoOrganizationDictVersionId = (state: IRootState) =>
  getPlanDataKnmPlanData(state)?.knoOrganizationErknm?.dictVersionId;
export const getPlanDataSupervisionDictVersionId = (state: IRootState) =>
  getPlanDataKnmPlanData(state)?.supervisionTypeErknm?.dictVersionId;
export const getPlanInfoTotalKnm = (state: IRootState) => getPlanInfo(state)?.totalKnm;
export const getPlanInfoFillInProcessKnm = (state: IRootState) => getPlanInfo(state)?.fillInProcessKnm;
export const getPlanInfoUnderConsiderationKnm = (state: IRootState) => getPlanInfo(state)?.underConsiderationKnm;
export const getPlanHistory = (state: IRootState) => getPlan(state).history;
export const getPlanVersions = (state: IRootState) => getPlanDataKnmPlanData(state)?.knmPlanVersions;

export const getPlanApprovedHistoryChanges = createSelector(getPlanHistory, history => {
  const approvedHistoryChanges: IPlanHistoryItem[] = [];

  history?.forEach(item => {
    item.changes.forEach(changes => {
      if (changes.field === '/erknmStatus') {
        const [firstName, lastName, secondName] = item.user.fullName.split(' ');
        let shortName = firstName;

        if (lastName) {
          shortName = `${firstName} ${lastName[0]}.${secondName ? ` ${secondName[0]}.` : ''}`;
        }
        approvedHistoryChanges.push({
          date: formatDate({ date: item.utcTimestamp, dateFormat: DATE_FORMAT_LONG_FNS }),
          title: checkValueInEnum<ErknmPlanStatusesEnum>(changes.newValue, Object(ErknmPlanStatusesEnum))
            ? getPlanErknmStatusTitleByStatus(changes.newValue) || ''
            : '',
          name: shortName,
        });
      }
    });
  });

  return approvedHistoryChanges;
});

export const getKnmPlanApprovedHistoryChanges = createSelector(getPlanHistory, history => {
  const approvedHistoryChanges: IPlanHistoryItem[] = [];

  history?.forEach(item => {
    item.changes.forEach(changes => {
      if (changes.field === '/erknmStatus') {
        const [firstName, lastName, secondName] = item.user.fullName.split(' ');
        let shortName = firstName;

        if (lastName) {
          shortName = `${firstName} ${lastName} ${secondName ? ` ${secondName[0]}.` : ''} ${
            item.user.username ? `(${item.user.username})` : ''
          }`;
        }
        approvedHistoryChanges.push({
          date: formatDate({
            date: item.utcTimestamp,
            dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            parseFormat: DATE_FORMAT_ISO_FNS,
          }),
          title: checkValueInEnum<ErknmPlanStatusesEnum>(changes.newValue, Object(ErknmPlanStatusesEnum))
            ? getPlanErknmStatusTitleByStatus(changes.newValue) || ''
            : '',
          name: shortName,
          oldValue: checkValueInEnum<ErknmPlanStatusesEnum>(changes.oldValue, Object(ErknmPlanStatusesEnum))
            ? getPlanErknmStatusTitleByStatus(changes.oldValue) || ''
            : '',
        });
      }
    });
  });

  return approvedHistoryChanges;
});

export const getPlanSignedHistoryChanges = createSelector(getPlanHistory, history => {
  const signedHistoryChanges: IPlanHistoryItem[] = [];

  history?.forEach(item => {
    item.changes.forEach(changes => {
      if (changes.field === '/signed') {
        const [firstName, lastName, secondName] = item.user.fullName.split(' ');
        let shortName = firstName;

        if (lastName) {
          shortName = `${firstName} ${lastName[0]}.${secondName ? ` ${secondName[0]}.` : ''}`;
        }
        if (
          item.user.username === UserRoleEnum.PROSECUTOR || item.user.username === UserRoleEnum.SUPERIOR_PROSECUTOR
            ? changes.newValue
            : true
        ) {
          signedHistoryChanges.push({
            date: formatDate({ date: item.utcTimestamp, dateFormat: DATE_FORMAT_LONG_FNS }),
            title: changes.newValue ? 'Добавлена подпись плана' : 'Подпись плана удалена',
            name: shortName,
          });
        }
      }
    });
  });

  return signedHistoryChanges;
});

export const getPlanKnoSupervisionUuid = (state: IRootState) => {
  const knoOrganizationDictVersionId = getPlanDataKnoOrganizationDictVersionId(state);
  const supervisionTypeDictVersionId = getPlanDataSupervisionDictVersionId(state);

  return getDictionariesKnoSupervisionUuidSelector(state)(knoOrganizationDictVersionId, supervisionTypeDictVersionId);
};

export const getPlanPositionsSelectOptions = (state: IRootState) => {
  const getDictionariesPositionsSelectOptions = makeGetDictionariesPositionsSelectOptions();
  const planSupervisionUuid = getPlanKnoSupervisionUuid(state);

  if (planSupervisionUuid) {
    return getDictionariesPositionsSelectOptions(state, planSupervisionUuid);
  }

  return undefined;
};

export const getPlanResponsesFromWeb = createSelector(getPlanData, data => data?.knmPlanData?.webNotificationInfos);
export const getPlanHistoryChanges = (state: IRootState) => state.plan.historyChanges;
export const getPlanIsPrivilegesKno = (state: IRootState) => state.plan.isPrivilegesKno;
export const getValidatedUnsignedKnmByPlan = (state: IRootState) => state.knm.validatedUnsignedKnmByPlan;

export const getPlanFormRef = (state: IRootState) => state.plan?.formRef;
